import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { cookieStorage } from '../../utils/secureCookies';
import { logger } from '../../utils/logger';
import { toAppError } from '@/types/errors';

// Platform detection
const isDesktop = typeof window !== 'undefined' && '__TAURI__' in window;

// Use environment variables for Supabase credentials
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// These are known placeholder patterns for Supabase credentials
const PLACEHOLDER_URLS = [
  'https://your-supabase-project.supabase.co',
  'your-supabase-url',
  'your-project-url.supabase.co',
  'your-supabase-project',
];

const PLACEHOLDER_KEYS = [
  'your-supabase-anon-key',
  'your-anon-key',
  'your-public-key',
];

// Verify environment variables are present and valid
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Throw an error to prevent the application from continuing with invalid credentials
  throw new Error('Missing required Supabase credentials. Check your environment variables.');
}

// Check if the URL is a known placeholder value
if (PLACEHOLDER_URLS.some(placeholder => SUPABASE_URL.includes(placeholder))) {
  throw new Error('Invalid Supabase URL. Please check your environment variables.');
}

// Check if the key is a known placeholder value
if (PLACEHOLDER_KEYS.some(placeholder => SUPABASE_ANON_KEY.toLowerCase().includes(placeholder.toLowerCase()))) {
  console.error('Error: Supabase anon key appears to be a placeholder value. Please provide a real anon key.');
  throw new Error('Invalid Supabase anon key. Please check your environment variables.');
}

// Additional validation: Supabase keys are usually JWT tokens starting with 'ey'
if (!SUPABASE_ANON_KEY.startsWith('ey')) {
  console.error('Error: Supabase anon key does not appear to be valid. Keys typically start with "ey".');
  throw new Error('Invalid Supabase anon key format. Please check your environment variables.');
}

const isDevelopment = import.meta.env.MODE === 'development';

// Control Supabase internal logging based on environment variables
// WARNING: Enabling Supabase debug logs will expose sensitive authentication data
const enableSupabaseLogs = import.meta.env.VITE_ENABLE_SUPABASE_LOGS === 'true';

// If environment variable is not set, default to enabling logs only in development with a warning
if (enableSupabaseLogs && isDevelopment) {
  console.warn(
    '⚠️ WARNING: Supabase debug logs are enabled. This may expose sensitive authentication data in your console.'
  );
  console.warn(
    'To disable, set VITE_ENABLE_SUPABASE_LOGS=false in your .env file.'
  );
}

export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: !isDesktop,
      // Use secure cookie storage instead of localStorage
      storage: {
        getItem: (key) => {
          try {
            const item = cookieStorage.getItem(key);
            if (isDevelopment) {
              logger.debug('[Supabase Storage] Getting item', { key, found: !!item });
            }
            return item;
          } catch (error) {
            // Convert to standard error type
            const appError = toAppError(error);
            logger.error('[Supabase Storage] Error getting item', appError, { key });
            return null;
          }
        },
        setItem: (key, value) => {
          try {
            if (isDevelopment) {
              logger.debug('[Supabase Storage] Setting item', { key });
            }
            cookieStorage.setItem(key, value);
          } catch (error) {
            // Convert to standard error type
            const appError = toAppError(error);
            logger.error('[Supabase Storage] Error setting item', appError, { key });
          }
        },
        removeItem: (key) => {
          try {
            if (isDevelopment) {
              logger.debug('[Supabase Storage] Removing item', { key });
            }
            cookieStorage.removeItem(key);
          } catch (error) {
            // Convert to standard error type
            const appError = toAppError(error);
            logger.error('[Supabase Storage] Error removing item', appError, { key });
          }
        },
      },
      flowType: 'implicit',
      // Only enable Supabase debug logs if explicitly requested via env var
      // WARNING: This will expose sensitive auth data in the console
      debug: enableSupabaseLogs
    },
    db: {
      schema: 'public'
    }
  }
);

// Add global handler for Supabase auth events
supabase.auth.onAuthStateChange((event, session) => {
  if (isDevelopment) {
    // Only log the event type and session existence, never the session data
    logger.debug('[Supabase Auth] Auth state changed', { event, hasSession: !!session });
  }
  
  if (event === 'TOKEN_REFRESHED') {
    if (isDevelopment) {
      logger.debug('[Supabase Auth] Token refreshed successfully');
    }
  }
  
  if (event === 'SIGNED_OUT') {
    if (isDevelopment) {
      logger.debug('[Supabase Auth] Cleaning up secure storage');
    }
    try {
      // Clear auth-related items from storage
      const authKeys = [
        'supabase.auth.token',
        'supabase.auth.refreshToken',
        'sb-' + SUPABASE_URL.split('//')[1].split('.')[0] + '-auth-token'
      ];
      
      authKeys.forEach(key => {
        cookieStorage.removeItem(key);
        if (isDevelopment) {
          logger.debug('[Supabase Auth] Removed', { key });
        }
      });
    } catch (error) {
      // Convert to standard error type
      const appError = toAppError(error);
      logger.error('[Supabase Auth] Error cleaning storage', appError);
    }
  }
});

// Add error handling for token refresh
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED' && !session) {
    logger.error('[Supabase Auth] Token refresh failed');
    // Force sign out to clear any invalid tokens
    supabase.auth.signOut().catch(error => {
      // Convert to standard error type
      const appError = toAppError(error);
      logger.error('[Supabase Auth] Error signing out after token refresh failure', appError);
    });
  }
});